import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useRef } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface GraphicSlide {
  url: string;
  thumbnailUrl?: string;
  slideOrder?: number;
}

interface GraphicCarouselProps {
  index: number;
  onSelect: (index: number) => void;
  slides: GraphicSlide[];
}

/**
 * Carousel for multi-slide graphics. Every slide sits side-by-side in a track
 * that slides via `translateX`, with prev/next, dots and a counter. Every
 * `<img>` mounts up front so each URL is fetched once and swaps are instant.
 *
 * It lives beside the panel rather than in the chat card because the panel is
 * where the artwork is now shown — but it had to come with it. Stacking the
 * slides vertically instead would have quietly dropped a control the owner
 * uses to read a carousel in the order it was made. Its own file, because the
 * media panel and the ad panel both show a carousel ad's creative and neither
 * should import the other to get it.
 */
export function GraphicCarousel({
  slides,
  index,
  onSelect,
}: GraphicCarouselProps) {
  const count = slides.length;
  const touchStart = useRef<number | null>(null);
  // Preserve the chosen slide across versions, clamping for shorter decks.
  const current = Math.min(index, Math.max(0, count - 1));

  const goPrev = () => onSelect((current - 1 + count) % count);
  const goNext = () => onSelect((current + 1) % count);

  return (
    <div className="space-y-2">
      <div
        className="relative w-full overflow-hidden rounded-md border touch-pan-y"
        onTouchStart={(event) => {
          touchStart.current = event.touches[0].clientX;
        }}
        onTouchEnd={(event) => {
          const start = touchStart.current;
          touchStart.current = null;
          if (start === null) return;
          const distance = event.changedTouches[0].clientX - start;
          if (Math.abs(distance) > 40) {
            if (distance < 0) goNext();
            else goPrev();
          }
        }}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <img
              key={slide.url}
              src={slide.url}
              alt={`Slide ${i + 1}`}
              className="w-full shrink-0"
              loading="lazy"
              draggable={false}
            />
          ))}
        </div>

        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-label="Previous slide"
          onClick={goPrev}
          className="absolute left-2 top-1/2 size-8 -translate-y-1/2 rounded-full bg-background/80 shadow-md backdrop-blur-sm transition-transform hover:scale-105 hover:bg-background"
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-label="Next slide"
          onClick={goNext}
          className="absolute right-2 top-1/2 size-8 -translate-y-1/2 rounded-full bg-background/80 shadow-md backdrop-blur-sm transition-transform hover:scale-105 hover:bg-background"
        >
          <ChevronRightIcon className="size-4" />
        </Button>
      </div>

      <div className="flex items-center justify-center gap-1.5">
        {slides.map((slide, i) => (
          <button
            key={slide.url}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === current ? 'true' : undefined}
            onClick={() => onSelect(i)}
            className={cn(
              'h-1.5 rounded-full transition-all',
              i === current
                ? 'w-5 bg-foreground'
                : 'w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70'
            )}
          />
        ))}
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Slide {current + 1} of {count}
      </p>
    </div>
  );
}
